import { DragDropContext, Droppable, DropResult } from "@hello-pangea/dnd";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { ReconsultaKanbanCard } from "./ReconsultaKanbanCard";
import { useDisponibilidadeBatch } from "@/hooks/useDisponibilidade";
import type { ReconsultaItem } from "@/hooks/useDisponibilidade";

export type ReconsultaBucket = "atrasadas" | "semana" | "proximas" | "feitas" | "canceladas";

interface ColunaConfig {
  id: ReconsultaBucket;
  nome: string;
  cor: "red" | "amber" | "blue" | "green" | "slate";
}

const COLUNAS: ColunaConfig[] = [
  { id: "atrasadas", nome: "Atrasadas", cor: "red" },
  { id: "semana", nome: "Esta semana", cor: "amber" },
  { id: "proximas", nome: "Próximas", cor: "blue" },
  { id: "feitas", nome: "Feitas", cor: "green" },
  { id: "canceladas", nome: "Canceladas", cor: "slate" },
];

const COR_CLASSES: Record<string, { bg: string; border: string; text: string; footer: string }> = {
  red: { bg: "bg-red-100", border: "border-red-300", text: "text-red-700", footer: "bg-red-200" },
  amber: { bg: "bg-amber-100", border: "border-amber-300", text: "text-amber-700", footer: "bg-amber-200" },
  blue: { bg: "bg-blue-100", border: "border-blue-300", text: "text-blue-700", footer: "bg-blue-200" },
  green: { bg: "bg-green-100", border: "border-green-300", text: "text-green-700", footer: "bg-green-200" },
  slate: { bg: "bg-slate-100", border: "border-slate-300", text: "text-slate-700", footer: "bg-slate-200" },
};

/** Bucket de uma reconsulta: estado (feito/cancelado) tem prioridade; senão, por data. */
export function bucketOfReconsulta(item: ReconsultaItem): ReconsultaBucket {
  if (item.status === "feito") return "feitas";
  if (item.status === "cancelado") return "canceladas";
  const agora = new Date();
  const inicioHoje = new Date(agora.getFullYear(), agora.getMonth(), agora.getDate());
  const daquiUmaSemana = new Date(inicioHoje);
  daquiUmaSemana.setDate(daquiUmaSemana.getDate() + 8); // inclui os próximos 7 dias
  const vencimento = new Date(item.due_at);
  if (vencimento < inicioHoje) return "atrasadas";
  if (vencimento < daquiUmaSemana) return "semana";
  return "proximas";
}

/** Pendentes que exigem ação agora (atrasadas + esta semana) — p/ o contador da aba. */
export function reconsultasUrgentes(items: ReconsultaItem[]): number {
  return items.filter((item) => {
    const bucket = bucketOfReconsulta(item);
    return bucket === "atrasadas" || bucket === "semana";
  }).length;
}

interface Props {
  items: ReconsultaItem[];
  onMove: (checkId: string, dest: ReconsultaBucket) => void;
  onReconsultar: (item: ReconsultaItem) => void;
  onCancelar: (checkId: string) => void;
}

export function ReconsultaKanbanBoard({ items, onMove, onReconsultar, onCancelar }: Props) {
  const { data: dispMap } = useDisponibilidadeBatch(items.map((item) => item.freelancer_id));

  const itensPorBucket = COLUNAS.reduce((acc, coluna) => {
    acc[coluna.id] = items.filter((item) => bucketOfReconsulta(item) === coluna.id);
    return acc;
  }, {} as Record<ReconsultaBucket, ReconsultaItem[]>);

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    if (result.source.droppableId === result.destination.droppableId) return;
    onMove(result.draggableId, result.destination.droppableId as ReconsultaBucket);
  };

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <div className="flex gap-4 overflow-x-auto pb-4">
        {COLUNAS.map((coluna) => {
          const cores = COR_CLASSES[coluna.cor];
          const itens = itensPorBucket[coluna.id] || [];
          return (
            <div key={coluna.id} className="shrink-0 flex flex-col w-72">
              <div className={cn("rounded-t-lg px-3 py-2 border", cores.bg, cores.border)}>
                <div className="flex items-center justify-between">
                  <h3 className={cn("font-semibold text-sm", cores.text)}>{coluna.nome}</h3>
                  <Badge
                    variant="secondary"
                    className={cn("text-xs font-medium px-2 py-0.5", cores.bg, cores.text)}
                  >
                    {itens.length}
                  </Badge>
                </div>
              </div>

              <Droppable droppableId={coluna.id}>
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={cn(
                      "min-h-[400px] flex-1 p-2 space-y-2 border border-t-0",
                      snapshot.isDraggingOver ? "bg-slate-100" : "bg-slate-50/50"
                    )}
                  >
                    {itens.map((item, index) => (
                      <ReconsultaKanbanCard
                        key={item.id}
                        item={item}
                        summary={dispMap?.get(item.freelancer_id) ?? null}
                        index={index}
                        onReconsultar={onReconsultar}
                        onCancelar={onCancelar}
                      />
                    ))}
                    {provided.placeholder}

                    {itens.length === 0 && (
                      <div className="flex items-center justify-center h-32 text-slate-400 text-sm text-center">
                        Nada aqui
                      </div>
                    )}
                  </div>
                )}
              </Droppable>

              <div className="bg-white/80 border border-slate-200 border-t-0 rounded-b-lg p-2">
                <div className={cn("h-1 rounded-full", cores.footer)} />
              </div>
            </div>
          );
        })}
      </div>
    </DragDropContext>
  );
}
